/** @file Rubber band from the press; release selects the pickable bones whose joints lie inside. */
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import { pointsInRect } from '@pose2d/editor-geometry/pointsInRect';
import type { Vec2 } from '@pose2d/math/types';
import type { GizmoDrawing, ToolContext, ToolGesture, ViewportPointer } from '../../panels/viewport/viewportTypes';
import { updateSelection } from '../../stores/updateSelection';
import { selectModeOf } from './selectModeOf';

/** Offset of tx inside a WORLD_STRIDE block (world: a, b, c, d, tx, ty). */
const WORLD_TX = 4;

/** The band's four edges in screen pixels, from the press corner to (x, y). */
function band(start: ViewportPointer, x: number, y: number): GizmoDrawing {
  const corners: Vec2[] = [[start.sx, start.sy], [x, start.sy], [x, y], [start.sx, y]];
  const lines = corners.map((c, i): [Vec2, Vec2] => [c, corners[(i + 1) % 4] ?? c]);
  return { handles: [], active: null, points: [], lines };
}

/** A press-point release is an empty marquee: a plain one clears bones and slots. */
export function marqueeGesture(ctx: ToolContext, start: ViewportPointer): ToolGesture {
  return {
    move(p) { ctx.setGizmo(band(start, p.sx, p.sy)); },
    release(p) {
      const joints = { coords: ctx.pose.world, stride: POSE_LAYOUT.WORLD_STRIDE, offset: WORLD_TX, count: ctx.data.bones.length };
      const hit = pointsInRect(joints, ctx.camera, { x0: start.sx, y0: start.sy, x1: p.sx, y1: p.sy }).filter((i) => ctx.maps.pickable[i] === 1);
      updateSelection({ bones: hit.map((i) => ctx.maps.boneIds[i] ?? ''), slots: [] }, selectModeOf(start));
      return [];
    },
    cancel() { ctx.setGizmo(null); },
  };
}
